import { useAuth } from "@clerk/clerk-react";
import { Box, Button, IconButton, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate, useParams } from "react-router-dom";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { fetcherAuth } from "utils/api";

import { OutreachAutomation, OutreachAutomationBrand } from "../../types";
import BrandReviewCard from "../BrandReviewCard";

const AUTOMATION_TYPE_ICONS: Record<
  string,
  { icon: string; bg: string; color: string }
> = {
  bento_database: {
    icon: "fa-solid fa-database",
    bg: "#e8f4fd",
    color: "#1976d2",
  },
  instagram_tracked_creators: {
    icon: "fa-brands fa-instagram",
    bg: "#fce8ec",
    color: "#c03d5e",
  },
  google_places: {
    icon: "fa-solid fa-location-dot",
    bg: "#d5f0e5",
    color: "#1a7a52",
  },
};

const PER_PAGE = 20;

interface DateGroup {
  runDate: string;
  brands: OutreachAutomationBrand[];
}

function groupByRunDate(brands: OutreachAutomationBrand[]): DateGroup[] {
  const dateMap = new Map<string, OutreachAutomationBrand[]>();
  for (const brand of brands) {
    if (!dateMap.has(brand.runDate)) {
      dateMap.set(brand.runDate, []);
    }
    dateMap.get(brand.runDate)!.push(brand);
  }

  // Sort dates descending (newest first)
  const sortedDates = Array.from(dateMap.keys()).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return sortedDates.map((runDate) => ({
    runDate,
    brands: dateMap.get(runDate)!,
  }));
}

function formatRunDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface AutomationBrandsDetailProps {
  automations: OutreachAutomation[];
}

export default function AutomationBrandsDetail({
  automations,
}: AutomationBrandsDetailProps) {
  const { automationId } = useParams<{ automationId: string }>();
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const automation = automations.find((a) => a.id === Number(automationId));

  const [brands, setBrands] = useState<OutreachAutomationBrand[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [approvingAll, setApprovingAll] = useState(false);
  const [approveAllOpen, setApproveAllOpen] = useState(false);

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  const fetchBrands = async (pageNum: number) => {
    if (!currentOrg?.id || !automationId) return;
    if (pageNum === 1) {
      setInitialLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const url = `/api/organization/${currentOrg.id}/outreach-automations/brands?per_page=${PER_PAGE}&page=${pageNum}&automation_id=${automationId}`;
      const res = await fetcherAuth(getToken, url, "GET", {});
      const newBrands = res.outreachAutomationBrands ?? [];
      if (pageNum === 1) {
        setBrands(newBrands);
      } else {
        setBrands((prev) => [...prev, ...newBrands]);
      }
      setTotal(res.total ?? 0);
      setPage(pageNum);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, automationId]);

  // Infinite scroll
  useEffect(() => {
    if (
      isPaginationRefVisible &&
      brands.length > 0 &&
      brands.length < total &&
      !loading &&
      !initialLoading
    ) {
      fetchBrands(page + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible, brands.length]);

  const handleApprove = async (brand: OutreachAutomationBrand) => {
    if (!currentOrg?.id) return;
    setUpdatingIds((prev) => new Set(prev).add(brand.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${brand.outreachAutomationId}/approve-brands`,
        "POST",
        {},
        { bentoBrandIds: [brand.bentoBrandId] },
      );
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setTotal((prev) => prev - 1);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
    }
  };

  const handleReject = async (brand: OutreachAutomationBrand) => {
    if (!currentOrg?.id) return;
    setUpdatingIds((prev) => new Set(prev).add(brand.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${brand.outreachAutomationId}/reject-brands`,
        "POST",
        {},
        { bentoBrandIds: [brand.bentoBrandId] },
      );
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setTotal((prev) => prev - 1);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
    }
  };

  const handleApproveAllConfirm = async () => {
    if (!currentOrg?.id || !automationId) return;
    setApproveAllOpen(false);
    setApprovingAll(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automationId}/approve-all-brands`,
        "POST",
      );
      setBrands([]);
      setTotal(0);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setApprovingAll(false);
    }
  };

  const isApprovalRequired = automation?.approvalMode === "approval_required";

  const dateGroups = useMemo(() => groupByRunDate(brands), [brands]);

  const typeConfig = automation
    ? AUTOMATION_TYPE_ICONS[automation.type] ?? {
        icon: "fa-solid fa-gear",
        bg: "rgba(1,67,57,0.1)",
        color: "#014339",
      }
    : { icon: "fa-solid fa-gear", bg: "rgba(1,67,57,0.1)", color: "#014339" };

  return (
    <Box sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              color: "#014339",
              "&:hover": { backgroundColor: "rgba(1,67,57,0.05)" },
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-arrow-left"
              sx={{ fontSize: 14 }}
            />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                backgroundColor: typeConfig.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Box
                component="span"
                className={typeConfig.icon}
                sx={{ fontSize: 13, color: typeConfig.color }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "28px",
                  color: "#014339",
                }}
              >
                {automation?.name ?? "Automation"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "rgba(1,67,57,0.6)",
                }}
              >
                {initialLoading ? (
                  <Skeleton
                    variant="text"
                    width={100}
                    sx={{ display: "inline-block" }}
                  />
                ) : (
                  <>
                    {total} brand{total !== 1 ? "s" : ""} queued
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
        {isApprovalRequired && brands.length > 0 && (
          <Button
            onClick={() => setApproveAllOpen(true)}
            disabled={approvingAll}
            sx={{
              backgroundColor: "#014339",
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              px: 2,
              height: 32,
              minWidth: 0,
              "&:hover": { backgroundColor: "#012e27" },
              "&:disabled": {
                backgroundColor: "#014339",
                opacity: 0.5,
                color: "white",
              },
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-check-double"
              sx={{ fontSize: 11, mr: 0.75 }}
            />
            {approvingAll ? "Approving..." : "Approve All"}
          </Button>
        )}
      </Box>

      {/* Content */}
      {initialLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rounded" width={200} height={24} />
          <Skeleton variant="rounded" width="100%" height={120} />
          <Skeleton variant="rounded" width="100%" height={120} />
          <Skeleton variant="rounded" width="100%" height={120} />
        </Box>
      ) : brands.length === 0 ? (
        <Box
          sx={{
            border: "2px dashed rgba(1,67,57,0.15)",
            borderRadius: "14px",
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "rgba(1,67,57,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-clipboard-check"
              sx={{ fontSize: 24, color: "rgba(1,67,57,0.3)" }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: "rgba(1,67,57,0.6)",
            }}
          >
            No brands queued
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "rgba(1,67,57,0.4)",
              textAlign: "center",
              maxWidth: 360,
            }}
          >
            When this automation discovers new brands, they'll appear here for
            review.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {dateGroups.map((dateGroup) => (
            <Box key={dateGroup.runDate}>
              {/* Date sub-header */}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(1,67,57,0.4)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.75,
                  ml: 0.5,
                }}
              >
                {formatRunDate(dateGroup.runDate)}
              </Typography>

              {/* Brand cards */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {dateGroup.brands.map((brand, index) => (
                  <Box
                    ref={
                      brands.length - 5 === brands.indexOf(brand)
                        ? paginationRef
                        : null
                    }
                    key={brand.id}
                  >
                    <BrandReviewCard
                      brand={brand}
                      variant="full"
                      onApprove={handleApprove}
                      onReject={handleReject}
                      isUpdating={updatingIds.has(brand.id)}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

          {/* Loading more indicator */}
          {loading && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Skeleton variant="rounded" width="100%" height={100} />
              <Skeleton variant="rounded" width="100%" height={100} />
            </Box>
          )}
        </Box>
      )}
      <ConfirmDialog
        header="Approve All Brands?"
        subheader={
          <Typography>
            This will approve all {total} pending brand
            {total !== 1 ? "s" : ""} and schedule their outreach emails. This
            action cannot be undone.
          </Typography>
        }
        open={approveAllOpen}
        handleConfirm={handleApproveAllConfirm}
        handleCancel={() => setApproveAllOpen(false)}
        handleClose={() => setApproveAllOpen(false)}
        confirmText="Approve All"
        cancelText="Cancel"
      />
    </Box>
  );
}
